import { useQuery } from "@tanstack/react-query";
import { apiJsonClient } from "@/lib/query/core/api-json-client";
import { usePrivateMutation } from "@/lib/query/core/use-private-mutation";
import { selectionKey } from "./selection";
import type {
  SimulatorCatalog,
  SimulatorInput,
  SimulatorPreview,
  SimulatorRun,
} from "../types/simulator.types";
const endpoint = "/admin/integration-simulator";
export function useSimulatorCatalog() {
  return useQuery({
    queryKey: ["admin", "integration-simulator", "catalog"],
    queryFn: async ({ signal }) => {
      const catalog = await apiJsonClient<SimulatorCatalog>(
        `${endpoint}/catalog`,
        { signal },
      );
      if (
        catalog.mode !== "isolated" ||
        !Array.isArray(catalog.providers) ||
        catalog.providers.some(
          (provider) =>
            !Array.isArray(provider.scenarios) ||
            !Array.isArray(provider.unsupportedScenarios) ||
            ![
              "real_application_callback_with_simulated_persistence",
              "defensive_webhook_stub_application_path",
            ].includes(provider.capability) ||
            ![
              "none",
              "simulated_authoritative_charge",
              "simulated_retrieve_only_for_contradictory_checkout_snapshots",
            ].includes(provider.externalLookup) ||
            provider.scenarios.some(
              (scenario) => !Array.isArray(scenario.cases),
            ),
        )
      )
        throw new Error("Invalid simulator catalog");
      return catalog;
    },
  });
}
export function useSimulatorPreview() {
  return usePrivateMutation({
    mutationFn: async (input: SimulatorInput) => {
      const response = await apiJsonClient<SimulatorPreview>(
        `${endpoint}/preview`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(input),
        },
      );
      if (
        response.mode !== "isolated" ||
        selectionKey(response) !== selectionKey(input) ||
        !Array.isArray(response.requests) ||
        !response.expected
      )
        throw new Error("Invalid simulator preview");
      return response;
    },
  });
}
export function useSimulatorRun() {
  return usePrivateMutation({
    mutationFn: async (input: SimulatorInput) => {
      const response = await apiJsonClient<SimulatorRun>(`${endpoint}/run`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (
        response.mode !== "isolated" ||
        selectionKey(response) !== selectionKey(input) ||
        !Array.isArray(response.steps) ||
        !Array.isArray(response.checks) ||
        typeof response.passed !== "boolean" ||
        response.steps.some(
          (step) =>
            !step.before ||
            !step.after ||
            !step.acceptance ||
            !Array.isArray(step.checks),
        )
      )
        throw new Error("Invalid simulator result");
      return response;
    },
  });
}
